import { useCallback, useEffect, useState } from "react";
import { getFxBasisTransparency } from "./fxRatesClient.js";

export function formatFxBasisLabel(basis) {
  if (!basis) return "USD conversion basis unavailable";
  if (basis.mode === "as_of") return `As of ${basis.asOfDate}`;
  if (basis.mode === "budget") return `FY${String(basis.fiscalYear).slice(-2)} budget`;
  return "Live rates";
}

export function formatFxBasisSummary(basis, liveFetchedAt = null) {
  if (!basis) return "Saved FX basis unavailable";
  if (basis.mode === "as_of") return `As-of date rates · ${basis.asOfDate}`;
  if (basis.mode === "budget") return `FY${basis.fiscalYear} budget basis`;
  const fetchedDate = liveFetchedAt ? String(liveFetchedAt).slice(0, 10) : null;
  return fetchedDate ? `Live rates · last fetched ${fetchedDate}` : "Live rates";
}

export function describeFxBasis(basis, rates = []) {
  const label = formatFxBasisLabel(basis);
  const details = (rates || [])
    .filter((rate) => rate.currency !== "USD")
    .map((rate) => {
      const date = rate.rateDate ? `, ${rate.rateDate}` : "";
      const carry = rate.carried ? `, carried from ${rate.rateDate}` : "";
      return `${rate.currency}: ${Number(rate.rateToUsd).toFixed(6)} currency units per USD, ${rate.source || "unknown source"}${date}${carry}`;
    });
  return details.length ? `${label}. ${details.join("; ")}` : label;
}

export function useFxConversionBasis() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const refresh = useCallback(() => {
    let active = true;
    setError(null);
    getFxBasisTransparency()
      .then((next) => { if (active) setResult(next); })
      .catch((cause) => { if (active) setError(cause); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let stop = refresh();
    const onInvalidate = () => {
      stop?.();
      stop = refresh();
    };
    window.addEventListener("ripple:fx-rates-invalidated", onInvalidate);
    return () => {
      stop?.();
      window.removeEventListener("ripple:fx-rates-invalidated", onInvalidate);
    };
  }, [refresh]);

  return {
    basis: result?.basis ?? null,
    rates: result?.rates ?? [],
    liveFetchedAt: result?.liveFetchedAt ?? null,
    loading: result == null && error == null,
    error,
    refresh,
  };
}
